import { Suspense, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import type { CurrentUser } from '../lib/auth';
import { usePostLoginRedirect } from '../lib/postLogin';
import { useTheme } from '../lib/useTheme';
import { ImpersonationBanner } from './settings/access-levels/ImpersonationBanner';
import { LeftNav } from './shell/LeftNav';
import { TopBar } from './shell/TopBar';

export function Layout() {
  const user = useOutletContext<CurrentUser>();
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Mounted here, not only in ThemePicker: the picker lives inside the account popover and is
  // unmounted almost all the time, so without this nothing would adopt the per-user preference
  // after /me resolves, and a `system` user would not follow a live OS switch.
  useTheme();

  // Sign-in always lands on `/`; this opens the page the reader was on before it (lib/postLogin).
  usePostLoginRedirect();

  return (
    <div className="flex min-h-screen bg-slate-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:border focus:border-brand-hover focus:bg-surface focus:px-3 focus:py-1.5 focus:text-sm focus:font-medium focus:text-brand-hover"
      >
        Skip to content
      </a>
      {/* Full-height sidebar on the left; header + content live in the right column,
          so the header never crosses over the sidebar. */}
      <LeftNav
        user={user}
        collapsed={navCollapsed}
        onToggle={() => setNavCollapsed((v) => !v)}
        mobileNavOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1">
          {/* Pages are lazy-loaded (route-level code splitting in App.tsx); this boundary keeps
              the nav/header visible while a page chunk downloads. */}
          <Suspense fallback={<section className="p-8 text-sm text-slate-500">Loading…</section>}>
            <Outlet context={user satisfies CurrentUser} />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
